import React from 'react';
import { Text, Button, InputText } from '@companycam/slab-web';
import styled from 'styled-components';
import {
  GooglePlaceAutocomplete,
  getAddressComponent,
} from 'components/shared/GooglePlaceAutocomplete';
import { type GooglePlaceDetails } from 'data/v3/data-contracts';
import { translate } from 'javascripts/translations/i18n';
import { getGoogleReviewUrl } from './shared/utils/getGoogleReviewUrl';
import { type GoogleReviewProfileData } from 'components/reviews/types';

// #region styled-components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_m);
  width: 100%;
`;

const ReviewProfileDataContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background-color: var(--cc_color_background_2);
  padding: var(--cc_size_spacing_l);
  border-radius: var(--cc_size_border_radius_l);
  flex-wrap: wrap;
`;

const LocationInfoContainer = styled.div`
  display: flex;
  flex: 1;
`;

const StyledInputText = styled(InputText)`
  width: 100%;
  margin-top: var(--cc_size_spacing_l);
`;

const PublicProfileData = styled.div`
  flex: 1;
`;
// #endregion

export type GooglePlace = {
  place_id: string;
  name: string;
  formatted_address: string;
  formatted_phone_number: string;
  url: string;
  website: string;
};

type ReviewProfileSearchProps = {
  reviewProfileData: GoogleReviewProfileData | null;
  setReviewProfileData: (reviewProfileData: GoogleReviewProfileData) => void;
};

const ReviewProfileSearch = ({
  setReviewProfileData,
  reviewProfileData,
}: ReviewProfileSearchProps) => {
  const handlePlaceChanged = (place: GooglePlaceDetails) => {
    setReviewProfileData({
      name: place.placeDetails.displayName.text,
      googlePlaceId: place.placeDetails.id,
      phoneNumber: place.placeDetails.nationalPhoneNumber,
      website: place.placeDetails.websiteUri
        ? new URL(place.placeDetails.websiteUri).origin
        : '',
      addressLine1: getAddressComponent(place.placeDetails.addressComponents, [
        'street_number',
        'route',
      ]),
      addressLine2: getAddressComponent(place.placeDetails.addressComponents, [
        'subpremise',
      ]),
      city: getAddressComponent(place.placeDetails.addressComponents, [
        'locality',
      ]),
      state: getAddressComponent(place.placeDetails.addressComponents, [
        'administrative_area_level_1',
      ]),
      postalCode: getAddressComponent(place.placeDetails.addressComponents, [
        'postal_code',
      ]),
      nickname: `${place.placeDetails.displayName.text} - ${getAddressComponent(
        place.placeDetails.addressComponents,
        ['locality'],
      )}`,
    });
  };

  const onNickNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReviewProfileData({
      ...reviewProfileData,
      nickname: e.target.value,
    });
  };

  const getFormattedAddress = () => {
    if (!reviewProfileData) {
      return '';
    }

    let address;

    if (reviewProfileData.addressLine1) {
      address = reviewProfileData.addressLine1;
    }

    if (reviewProfileData.addressLine2) {
      address += `, ${reviewProfileData.addressLine2}`;
    }

    if (reviewProfileData.city) {
      address += `, ${reviewProfileData.city}`;
    }

    if (reviewProfileData.state) {
      address += `, ${reviewProfileData.state}`;
    }

    if (reviewProfileData.postalCode) {
      address += ` ${reviewProfileData.postalCode}`;
    }

    return address;
  };

  return (
    <Container>
      <GooglePlaceAutocomplete
        testId="google-place-autocomplete"
        onPlaceSelect={handlePlaceChanged}
        placeholder={translate('project.reviews.reviewProfile.instructions')}
        persistSearchValue
      />
      {reviewProfileData ? (
        <ReviewProfileDataContainer>
          <LocationInfoContainer>
            <PublicProfileData>
              <Text as="p" weight={700} family="heading">
                {reviewProfileData.name}
              </Text>
              <Text as="p" family="heading">
                {getFormattedAddress()}
              </Text>
              <Text as="p" family="heading">
                {reviewProfileData.phoneNumber}
              </Text>
              <Text as="p" family="heading">
                {reviewProfileData.website}
              </Text>
            </PublicProfileData>
            <Button
              data-testid="open-google-maps-button"
              href={getGoogleReviewUrl(reviewProfileData.googlePlaceId)}
              target="_blank"
              rel="noreferrer"
              color="subtle"
              icon={{
                name: 'open-in-new',
                position: 'after',
              }}
            >
              {translate('project.reviews.reviewProfile.openGoogleMaps')}
            </Button>
          </LocationInfoContainer>
          <StyledInputText
            autoFocus
            value={reviewProfileData.nickname}
            id="public-profile-nickname"
            label={translate('project.reviews.reviewProfile.nickNameLabel')}
            onChange={onNickNameChange}
            placeholder={translate(
              'project.reviews.reviewProfile.nickNamePlaceholder',
            )}
          />
        </ReviewProfileDataContainer>
      ) : null}
    </Container>
  );
};

export default ReviewProfileSearch;
